import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  CueScript,
  PrompterSettings,
  SessionState,
  ViewRole,
  ControlCommand,
  RoomCredentials,
  SessionTransport,
  TransportStatus,
  ParsedScript,
  ScrollMode,
} from "./types";
import { defaultSettings, demoScript, initialState } from "./defaults";
import { parseScript, sanitizeScript } from "./core/script";
import { SpeechMatcher } from "./core/matcher";
import {
  createRoom,
  createTransport,
  createViewUrl,
  readViewCredentials,
  getNetworkUrls,
  ensureLocalCredentials,
  supportsLocalSynchronization,
} from "./transport";
import { registerOfflineApp } from "./transport/pwa";
import {
  applySessionCommand,
  advanceSession,
  normalizeSettings,
} from "./studio/session";
import { importScriptFile, normalizeScript } from "./studio/imports";
import type { BatchExportFormat } from "./studio/batch-export";
import { useRehearsal, type RehearsalController } from "./studio/useRehearsal";
import { useRecorder, type RecordingController } from "./studio/useRecorder";
import { useHistory, prepareRestoredVersions, type ScriptVersion } from "./studio/useHistory";
import { useTakeJournal, prepareRestoredTakes, type TakeJournal } from "./studio/useTakeJournal";
import { isExplicitRoomRoute, shouldRecoverCachedRoom } from "./studio/room-recovery";
import { readWorkspaceSettings } from "./studio/appearance";
import { TranscriptPositionGuard } from "./speech/transcript-guard";
import type { SpeechTranscript, SpeechEngine } from "./speech/engines";
import { normalizeLaunchpad, readLaunchpad, LAUNCHPAD_STORAGE, LAUNCHPAD_CHANGED } from "./launchpad/config";
import { useProductionNotes } from "./production-notes/useProductionNotes";
import { validProductionDocument, saveProductionDocument, NOTES_STORAGE_PREFIX } from "./production-notes/model";
import { noteText } from "./production-notes/controller";
import type { AgentGrant, AgentManagementRequest, AgentManagementResult, AgentRequest, AgentResult } from "./agent/types";
import { createAgentExecutor, type AgentHost } from "./agent/executor";
import { persistAgentValues } from "./agent/persistence";
import {
  PROJECT_FOLDERS_STORAGE, MAX_PROJECT_FOLDERS, cleanProjectName, projectNameExists,
  normalizeProjectFolders, moveProjectScript, restoreProjectFolders,
  validProjectFoldersBackup, type ProjectFolder, type ProjectFoldersState,
} from "./studio/project-folders";

export interface SpeechStatus {
  active: boolean;
  preparing?: boolean;
  status: string;
  transcript?: string;
  error?: string;
  progress?: number;
  engine?: string;
  confidence?: number;
}
export interface AudioStatus {
  busy: boolean;
  progress?: number;
  transcript?: string;
  error?: string;
}
export interface StudioController {
  agentGrants: AgentGrant[];
  agentActivity: { operation: string; at: number; ok: boolean }[];
  agentManage: (request: AgentManagementRequest) => Promise<AgentManagementResult>;
  setAgentEditingLocked: (locked: boolean) => void;
  state: SessionState;
  parsed: ParsedScript;
  scripts: CueScript[];
  projectFolders: ProjectFolder[];
  createProjectFolder: (name: string) => string | null;
  renameProjectFolder: (id: string, name: string) => void;
  deleteProjectFolder: (id: string) => void;
  moveScriptToFolder: (scriptId: string, folderId: string | null) => void;
  status: TransportStatus;
  speechStatus: SpeechStatus;
  credentials: RoomCredentials | null;
  role: ViewRole;
  notice: string | null;
  hasSnapshot: boolean;
  networkUrls: string[];
  lanEnabled: boolean;
  storageHealthy: boolean;
  command: (command: ControlCommand) => void;
  scrubTo: (position: number) => void;
  updateScript: (patch: Partial<CueScript>) => void;
  selectScript: (id: string) => void;
  createScript: (folderId?: string | null) => void;
  duplicateScript: (id: string) => void;
  deleteScript: (id: string) => void;
  importScripts: (files: File[], folderId?: string | null) => Promise<void>;
  exportScript: (format: "html" | "txt" | "json", scriptId?: string) => void;
  exportBatch: (ids: string[], format: BatchExportFormat, options: { signal: AbortSignal; onProgress: (done: number, total: number) => void }) => Promise<void>;
  exportBackup: () => void;
  importBackup: (file: File) => Promise<void>;
  updateSettings: (settings: Partial<PrompterSettings>) => void;
  selectScrollMode: (mode: ScrollMode) => Promise<void>;
  startSpeech: () => Promise<void>;
  stopSpeech: () => void;
  feedTranscript: (text: string, final?: boolean, id?: string) => void;
  transcribeFile: (file: File) => Promise<void>;
  cancelAudio: () => void;
  audioStatus: AudioStatus;
  microphones: { id: string; label: string }[];
  loadMicrophones: () => Promise<void>;
  microphoneId: string;
  setMicrophoneId: (id: string) => void;
  speechModel: "tiny" | "base";
  setSpeechModel: (model: "tiny" | "base") => void;
  rehearsal: RehearsalController;
  recording: RecordingController;
  modelStatus: {
    busy: boolean;
    progress: number;
    ready: boolean;
    error?: string;
  };
  prepareModel: () => Promise<void>;
  cancelModel: () => void;
  clearModels: () => Promise<void>;
  updateAvailable: boolean;
  applyUpdate: () => void;
  rundown: string[];
  setRundown: (value: string[] | ((previous: string[]) => string[])) => void;
  versions: ScriptVersion[];
  saveVersion: (label?: string) => void;
  restoreVersion: (id: string) => void;
  trash: CueScript[];
  restoreTrash: (id: string) => void;
  journal: TakeJournal;
  productionNotes: ReturnType<typeof useProductionNotes>;
  connectRelay: (url: string) => Promise<void>;
  openOutput: () => void;
  getViewUrl: (role: ViewRole, origin?: string) => string;
  copyText: (text: string) => Promise<void>;
  notify: (message: string) => void;
}
const LIBRARY = "cue-studio-library-v1",
  SETTINGS = "cue-studio-settings-v1",
  SESSION = "cue-studio-session-v1",
  ROOM = "cue-studio-room-v1";
class WorkspaceStorageConflict extends Error {}
function readLibrary(): CueScript[] {
  try {
    const values = JSON.parse(localStorage.getItem(LIBRARY) || "null");
    if (Array.isArray(values)) {
      const scripts = values
        .map((x) => normalizeScript(x))
        .filter((x): x is CueScript => !!x);
      if (scripts.length) return scripts;
    }
  } catch {}
  return [{ ...demoScript }];
}
function readSettings(): PrompterSettings {
  try {
    return readWorkspaceSettings(localStorage);
  } catch {
    return { ...defaultSettings };
  }
}
function readCachedRoom(): RoomCredentials | null {
  try {
    return readViewCredentials(
      "#room=" + encodeURIComponent(sessionStorage.getItem(ROOM) || ""),
    );
  } catch { return null; }
}
function queryRoom(): { role: ViewRole; credentials: RoomCredentials | null; explicitRoute: boolean } {
  const role = new URLSearchParams(location.search).get("view");
  let credentials = readViewCredentials();
  if (!credentials && role !== "talent" && role !== "remote") {
    credentials = readCachedRoom();
  }
  return {
    role: role === "talent" || role === "remote" ? role : "operator",
    credentials,
    explicitRoute: isExplicitRoomRoute(location.search, location.hash),
  };
}
function readSession(scripts: CueScript[]): SessionState {
  try {
    const saved = JSON.parse(localStorage.getItem(SESSION) || "{}");
    const script =
      scripts.find((x) => x.id === saved.scriptId) || scripts[0] || demoScript;
    const state = initialState(script, readSettings());
    return {
      ...state,
      revision: Date.now(),
      elapsed: Number.isFinite(saved.elapsed) ? Math.max(0, saved.elapsed) : 0,
      wordIndex: Math.max(
        0,
        Math.min(
          Math.max(0, parseScript(script.html).words.length - 1),
          Number.isFinite(saved.wordIndex) ? saved.wordIndex : 0,
        ),
      ),
    };
  } catch {
    return {
      ...initialState(scripts[0] || demoScript, readSettings()),
      revision: Date.now(),
    };
  }
}
function download(name: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function useStudio(): StudioController {
  const route = useMemo(queryRoom, []);
  const role = route.role;
  const [rundown, setRundown] = useState<string[]>(() => {
    try {
      const x = JSON.parse(localStorage.getItem("cue-rundown") || "[]");
      return Array.isArray(x)
        ? x.filter((v) => typeof v === "string").slice(0, 1000)
        : [];
    } catch {
      return [];
    }
  });
  const [trash, setTrash] = useState<CueScript[]>(() => {
    try {
      const x = JSON.parse(localStorage.getItem("cue-studio-trash-v1") || "[]");
      return Array.isArray(x)
        ? x
            .map((v) => normalizeScript(v))
            .filter((v): v is CueScript => !!v)
            .slice(0, 100)
        : [];
    } catch {
      return [];
    }
  });
  const [scripts, setScripts] = useState(readLibrary);
  const [initialProjectStorage] = useState(() => {
    let serialized: string | null = null, library: string | null = null;
    try {
      library = localStorage.getItem(LIBRARY);
      serialized = localStorage.getItem(PROJECT_FOLDERS_STORAGE);
      return { serialized, library, state: normalizeProjectFolders(JSON.parse(serialized || "null"), scripts.map(script => script.id)) };
    }
    catch { return { serialized, library, state: { version: 1 as const, folders: [] } }; }
  });
  const [projectFoldersState, setProjectFoldersState] = useState(initialProjectStorage.state);
  const projectStorageBaseline = useRef(initialProjectStorage.serialized);
  const libraryStorageBaseline = useRef(initialProjectStorage.library);
  const [state, setState] = useState(() => readSession(scripts));
  const stateRef = useRef(state);
  stateRef.current = state;
  const [credentials, setCredentials] = useState<RoomCredentials | null>(
    route.credentials,
  );
  const [localKeyFailure, setLocalKeyFailure] = useState<string | null>(null);
  const needsLocalKey = supportsLocalSynchronization() && role === "operator" && !!credentials?.relayUrl &&
    !!credentials.controllerToken && (!credentials.localControllerKey || !credentials.verificationKey);
  const [networkUrls, setNetworkUrls] = useState<string[]>([]);
  const [lanEnabled, setLanEnabled] = useState(false);
  const [hasSnapshot, setHasSnapshot] = useState(role === "operator");
  const [storageIssues, setStorageIssues] = useState<string[]>([]);
  const markStorage = useCallback((area: string, healthy: boolean) => {
    setStorageIssues((current) => {
      if (current.includes(area) === !healthy) return current;
      return healthy
        ? current.filter((item) => item !== area)
        : [...current, area];
    });
  }, []);
  const [status, setStatus] = useState<TransportStatus>({
    kind: "local",
    connected: false,
    peers: [],
    latency: null,
    error: null,
  });
  const [speechStatus, setSpeechStatus] = useState<SpeechStatus>({
    active: false,
    status: "Microphone off",
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [modelStatus, setModelStatus] = useState<{
    busy: boolean;
    progress: number;
    ready: boolean;
    error?: string;
  }>({ busy: false, progress: 0, ready: false });
  const modelAbort = useRef<AbortController | null>(null);
  const modelReadGeneration = useRef(0);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const activateUpdate = useRef<(() => void) | null>(null);
  const rehearsalStop = useRef<() => void>(() => {});
  const [audioStatus, setAudioStatus] = useState<AudioStatus>({ busy: false });
  const audioAbort = useRef<AbortController | null>(null);
  const [microphones, setMicrophones] = useState<
    { id: string; label: string }[]
  >([]);
  const [microphoneId, setMicrophoneId] = useState("");
  const [speechModel, setSpeechModel] = useState<"tiny" | "base">("tiny");
  const transport = useRef<SessionTransport | null>(null);
  const [agentGrants, setAgentGrants] = useState<AgentGrant[]>([]);
  const approvedAgents = useRef<AgentGrant[]>([]);
  const agentConnectionGeneration = useRef(0);
  const agentEditingLocked = useRef(false);
  const [agentActivity, setAgentActivity] = useState<{ operation: string; at: number; ok: boolean }[]>([]);
  const agentExecuteRef = useRef<(request: AgentRequest) => Promise<AgentResult>>(async () => ({ ok: false, error: { code: "unavailable", message: "The operator is still loading." } }));
  const agentHostRef = useRef<AgentHost | null>(null);
  const agentExecutor = useRef<ReturnType<typeof createAgentExecutor> | null>(null);
  const setAgentEditingLocked = useCallback((locked: boolean) => { agentEditingLocked.current = locked; }, []);
  const clearAgentGrants = useCallback(() => {
    agentConnectionGeneration.current++;
    approvedAgents.current = [];
    setAgentGrants([]);
  }, []);
  const agentManage = useCallback(async (request: AgentManagementRequest): Promise<AgentManagementResult> => {
    if (role !== "operator") throw new Error("Only the operator window can approve AI clients.");
    // Revoke locally first, including when the network is unavailable.
    if (request.action === "revoke-all") clearAgentGrants();
    else if (request.action === "revoke") {
      approvedAgents.current = approvedAgents.current.filter(grant => grant.id !== request.grantId);
      setAgentGrants([...approvedAgents.current]);
    }
    const activeTransport = transport.current;
    if (!activeTransport) throw new Error("The live relay is not connected.");
    const generation = agentConnectionGeneration.current;
    const result = await activeTransport.agentManage(request);
    if (generation !== agentConnectionGeneration.current || activeTransport !== transport.current) throw new Error("The connection changed. Pair the client again.");
    if (request.action === "oauth-approve" && !result.grant) throw new Error("No approval was received. Review this request again.");
    if ((request.action === "pair" || request.action === "oauth-approve") && result.grant) {
      const grant = result.grant;
      if (!grant.id || !grant.name || !Array.isArray(grant.scopes) || !Number.isFinite(grant.expiresAt) || grant.expiresAt <= Date.now() || grant.scopes.some(scope => !request.scopes?.includes(scope))) throw new Error("The relay returned an invalid approval.");
      approvedAgents.current = [...approvedAgents.current.filter(item => item.id !== grant.id && item.expiresAt > Date.now()), grant];
      if (request.action === "oauth-approve") {
        // Commit the browser's explicit approval before the relay releases an
        // authorization code. A fast client must never race this local gate.
        try {
          const ready = await activeTransport.agentManage({ action: "oauth-ready", grantId: grant.id });
          if (generation !== agentConnectionGeneration.current || activeTransport !== transport.current) throw new Error("The connection changed. Connect the client again.");
          if (!ready.ready) throw new Error("Approval could not finish. Start the connection again in your AI client.");
        } catch (error) {
          approvedAgents.current = approvedAgents.current.filter(item => item.id !== grant.id);
          setAgentGrants([...approvedAgents.current]);
          void activeTransport.agentManage({ action: "revoke", grantId: grant.id }).catch(() => {});
          throw error;
        }
      }
    } else if (result.grants) {
      // A relay list may only narrow approvals already minted by this browser.
      approvedAgents.current = approvedAgents.current.flatMap(local => {
        const remote = result.grants!.find(grant => grant.id === local.id && grant.expiresAt > Date.now());
        return remote ? [{ ...local, expiresAt: Math.min(local.expiresAt, remote.expiresAt), scopes: local.scopes.filter(scope => remote.scopes.includes(scope)) }] : [];
      });
    }
    setAgentGrants([...approvedAgents.current]);
    return { ...result, grants: [...approvedAgents.current] };
  }, [role, clearAgentGrants]);
  const roomRecoveryAttempted = useRef(false);
  const roomConnectionGeneration = useRef(0);
  const roomConnectionBusy = useRef(false);
  const engine = useRef<SpeechEngine | null>(null);
  const transcriptGuard = useRef(new TranscriptPositionGuard());
  const speechGeneration = useRef(0);
  const speechMounted = useRef(true);
  const speechRequest = useRef<{ key: string; promise: Promise<void> } | null>(null);
  // getUserMedia cannot be aborted while its permission prompt is pending.
  // Wait for an old request to release its eventual stream before opening another.
  const speechStartBarrier = useRef<Promise<void> | null>(null);
  const overrideUntil = useRef(0);
  const speaking = useRef(false);
  const noticeTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const parsed = useMemo(
    () => parseScript(state.script.html),
    [state.script.html],
  );
  const parsedRef = useRef(parsed);
  parsedRef.current = parsed;
  const matcher = useRef<SpeechMatcher | null>(null);
  if (matcher.current === null)
    matcher.current = new SpeechMatcher(parsed.words);
  useEffect(() => {
    matcher.current = new SpeechMatcher(parsed.words);
    matcher.current!.setPosition(stateRef.current.wordIndex);
    transcriptGuard.current.invalidate();
    engine.current?.resetTracking?.();
  }, [parsed]);
  const notify = useCallback((message: string) => {
    setNotice(message);
    clearTimeout(noticeTimeout.current);
    noticeTimeout.current = setTimeout(() => setNotice(null), 5500);
  }, []);
  const history = useHistory(notify, role === "operator");
  const rawJournal = useTakeJournal(state, role === "operator");
  const productionNotes = useProductionNotes({ enabled: role === "operator", credentials, scripts, takes: rawJournal.takes });
  const journalTakes = useMemo(() => {
    const entries = Object.values(productionNotes.document?.records || {});
    const notes = new Map(entries.filter(r => r.fields.kind?.value === 'take').map(r => [r.fields.sourceTakeId?.value, r.fields.notes?.value]));
    return rawJournal.takes.map(t => notes.has(t.id) ? { ...t, notes: noteText(notes.get(t.id)) } : t);
  }, [rawJournal.takes, productionNotes.document]);
  const journal: TakeJournal = { ...rawJournal, takes: journalTakes,
    setNotes: (id, notes) => {
      rawJournal.setNotes(id, notes);
      const record = Object.entries(productionNotes.getDocument()?.records || {}).find(([,r]) => r.fields.sourceTakeId?.value === id);
      if (record) productionNotes.setRecordFields(record[0], { notes });
    },
    exportJournal: format => rawJournal.exportJournal(format, journalTakes),
  };
  const workspaceSnapshot = useMemo(
    () => ({
      scripts,
      projectFoldersState,
      rundown,
      versions: history.versions,
      trash,
      takes: journal.takes,
      productionNotes: productionNotes.document,
    }),
    [scripts, projectFoldersState, rundown, history.versions, trash, journal.takes, productionNotes.document],
  );
  const workspaceRef = useRef(workspaceSnapshot);
  workspaceRef.current = workspaceSnapshot;
  const fileImportBusy = useRef(false);
  const storageHealthy =
    !storageIssues.length && !history.storageError && !journal.storageError && !productionNotes.storageError;
  const checkLibraryStorage = useCallback(() => {
    if (localStorage.getItem(LIBRARY) !== libraryStorageBaseline.current)
      throw new WorkspaceStorageConflict("Scripts changed in another window. Reload this workspace before making changes; the other window’s work was kept.");
  }, []);
  const checkProjectStorage = useCallback(() => {
    const serialized = localStorage.getItem(PROJECT_FOLDERS_STORAGE);
    if (serialized === projectStorageBaseline.current) return;
    let value: unknown;
    try { value = JSON.parse(serialized || "null"); }
    catch { throw new WorkspaceStorageConflict("Saved projects changed in another window and could not be read. Reload this workspace before making changes."); }
    if (serialized !== null && !validProjectFoldersBackup(value))
      throw new WorkspaceStorageConflict("Saved projects changed in another window and use an unsupported format. Reload this workspace before making changes.");
    // Preserve memberships for scripts added by the other window. A subsequent
    // library mutation separately checks its baseline and requires a reload.
    const next = normalizeProjectFolders(value);
    projectStorageBaseline.current = serialized;
    workspaceRef.current = { ...workspaceRef.current, projectFoldersState: next };
    setProjectFoldersState(next);
    throw new WorkspaceStorageConflict("Projects changed in another window. The latest folders are now shown. Try your change again.");
  }, []);
  const commitProjectFolders = useCallback((next: ProjectFoldersState): boolean => {
    try {
      checkProjectStorage();
      checkLibraryStorage();
      persistAgentValues(localStorage, { [PROJECT_FOLDERS_STORAGE]: next });
      projectStorageBaseline.current = JSON.stringify(next);
      workspaceRef.current = { ...workspaceRef.current, projectFoldersState: next };
      setProjectFoldersState(next);
      markStorage("projects", true);
      return true;
    } catch (error) {
      if (!(error instanceof WorkspaceStorageConflict)) markStorage("projects", false);
      notify(error instanceof Error ? error.message : "Project changes could not be saved. Your projects were kept.");
      return false;
    }
  }, [markStorage, notify, checkProjectStorage, checkLibraryStorage]);
  const commitScriptLibrary = useCallback((nextScripts: CueScript[], folders = workspaceRef.current.projectFoldersState, extra: Record<string, unknown> = {}): boolean => {
    const nextFolders = normalizeProjectFolders(folders, nextScripts.map(script => script.id));
    try {
      checkProjectStorage();
      checkLibraryStorage();
      const values: Record<string, unknown> = { ...extra, [LIBRARY]: nextScripts, [PROJECT_FOLDERS_STORAGE]: nextFolders };
      // Notes replicas merge storage events as CRDT updates: publishing notes
      // before a later failure cannot be undone by writing an older document.
      // Always write notes last, after every key that could still fail.
      for (const [key, value] of Object.entries(extra)) {
        if (key.startsWith(NOTES_STORAGE_PREFIX)) { delete values[key]; values[key] = value; }
      }
      persistAgentValues(localStorage, values);
      projectStorageBaseline.current = JSON.stringify(nextFolders);
      libraryStorageBaseline.current = JSON.stringify(nextScripts);
      workspaceRef.current = { ...workspaceRef.current, scripts: nextScripts, projectFoldersState: nextFolders };
      setScripts(nextScripts);
      setProjectFoldersState(nextFolders);
      markStorage("projects", true);
      markStorage("library", true);
      return true;
    } catch (error) {
      if (!(error instanceof WorkspaceStorageConflict)) {
        markStorage("projects", false);
        markStorage("library", false);
      }
      notify(error instanceof Error ? error.message : "Script changes could not be saved. Your library was kept.");
      return false;
    }
  }, [markStorage, notify, checkProjectStorage, checkLibraryStorage]);
  const createProjectFolder = useCallback((value: string): string | null => {
    if (role !== "operator") return null;
    const name = cleanProjectName(value);
    const current = workspaceRef.current.projectFoldersState;
    if (!name) { notify("Give the project a name."); return null; }
    if (projectNameExists(current.folders, name)) { notify("A project already uses this name. Choose another name."); return null; }
    if (current.folders.length >= MAX_PROJECT_FOLDERS) { notify(`Your workspace can hold ${MAX_PROJECT_FOLDERS} projects.`); return null; }
    const id = crypto.randomUUID();
    return commitProjectFolders({ version: 1, folders: [...current.folders, { id, name, scriptIds: [] }] }) ? id : null;
  }, [role, notify, commitProjectFolders]);
  const renameProjectFolder = useCallback((id: string, value: string) => {
    if (role !== "operator") return;
    const current = workspaceRef.current.projectFoldersState;
    const name = cleanProjectName(value);
    if (!current.folders.some(folder => folder.id === id)) return;
    if (!name) { notify("Give the project a name."); return; }
    if (projectNameExists(current.folders, name, id)) { notify("A project already uses this name. Choose another name."); return; }
    commitProjectFolders({ version: 1, folders: current.folders.map(folder => folder.id === id ? { ...folder, name } : folder) });
  }, [role, notify, commitProjectFolders]);
  const deleteProjectFolder = useCallback((id: string) => {
    if (role !== "operator") return;
    const current = workspaceRef.current.projectFoldersState;
    if (!current.folders.some(folder => folder.id === id)) return;
    if (commitProjectFolders({ version: 1, folders: current.folders.filter(folder => folder.id !== id) }))
      notify("Project folder removed. Its scripts are still in your library.");
  }, [role, notify, commitProjectFolders]);
  const moveScriptToFolder = useCallback((scriptId: string, folderId: string | null) => {
    if (role !== "operator") return;
    if (!workspaceRef.current.scripts.some(script => script.id === scriptId)) {
      notify("This script is no longer in your library."); return;
    }
    try { commitProjectFolders(moveProjectScript(workspaceRef.current.projectFoldersState, scriptId, folderId)); }
    catch (error) { notify(error instanceof Error ? error.message : "The script could not be moved."); }
  }, [role, notify, commitProjectFolders]);
  useEffect(() => {
    if (role !== "operator") return;
    try {
      localStorage.setItem("cue-rundown", JSON.stringify(rundown));
      localStorage.setItem("cue-studio-trash-v1", JSON.stringify(trash));
      markStorage("recovery", true);
    } catch {
      markStorage("recovery", false);
      notify("Recovery storage is full. Export a workspace backup.");
    }
  }, [rundown, trash, role, notify, markStorage]);
  const lastVersion = useRef<Record<string, number>>({});
  const mutate = useCallback((change: (s: SessionState) => SessionState) => {
    const prev = stateRef.current,
      changed = change(prev);
    if (changed === prev) return;
    const next = { ...changed, revision: prev.revision + 1 };
    stateRef.current = next;
    setState(next);
  }, []);
  const stopSpeech = useCallback(() => {
    transcriptGuard.current.invalidate();
    speechGeneration.current++;
    speechRequest.current = null;
    engine.current?.stop();
    engine.current = null;
    speaking.current = false;
    setSpeechStatus({ active: false, status: "Microphone off" });
  }, []);
  const applyCommand = useCallback(
    (cmd: ControlCommand) => {
      if (cmd.type === "seek" || cmd.type === "nudge" || cmd.type === "reset")
        overrideUntil.current =
          performance.now() + stateRef.current.settings.manualOverrideMs;
      const next = applySessionCommand(
        stateRef.current,
        cmd,
        parsedRef.current,
      );
      if (["seek", "nudge", "reset", "play", "pause", "toggle"].includes(cmd.type) || (cmd.type === "cue" && cmd.cue && cmd.pause)) {
        transcriptGuard.current.invalidate();
        engine.current?.resetTracking?.();
      }
      if (
        next.settings.mode !== stateRef.current.settings.mode ||
        next.settings.speechEngine !== stateRef.current.settings.speechEngine ||
        next.settings.language !== stateRef.current.settings.language ||
        next.settings.streamingModel !== stateRef.current.settings.streamingModel
      ) stopSpeech();
      if (cmd.type === "reset") matcher.current!.reset(0);
      else if (cmd.type === "seek" || cmd.type === "nudge")
        matcher.current!.setPosition(next.wordIndex);
      mutate(() => next);
    },
    [mutate, stopSpeech],
  );
  const command = useCallback(
    (cmd: ControlCommand) => {
      if (role === "operator") applyCommand(cmd);
      else if (role === "remote") transport.current?.command(cmd);
    },
    [role, applyCommand],
  );
  const scrubTo = useCallback((position: number) => {
    if (role !== "operator" || !Number.isFinite(position)) return;
    const value = Math.max(0, Math.min(Math.max(0, parsedRef.current.words.length - 1), position));
    const next = applySessionCommand(stateRef.current, { type: "seek", wordIndex: Math.floor(value) }, parsedRef.current);
    overrideUntil.current = performance.now() + next.settings.manualOverrideMs;
    transcriptGuard.current.invalidate();
    engine.current?.resetTracking?.();
    matcher.current!.setPosition(next.wordIndex);
    mutate(() => ({ ...next, wordProgress: value - next.wordIndex }));
  }, [role, mutate]);
  useEffect(() => {
    if (role !== "operator" || credentials) return;
    let cancelled = false;
    createRoom()
      .then((c) => {
        if (!cancelled) setCredentials(c);
      })
      .catch(() => notify("Could not create a session. Reload to try again."));
    return () => {
      cancelled = true;
    };
  }, [role, credentials, notify]);
  useEffect(() => {
    if (role === "operator" && credentials) {
      try {
        sessionStorage.setItem(ROOM, JSON.stringify(credentials));
      } catch {}
    }
  }, [credentials, role]);
  useEffect(() => {
    if (!needsLocalKey || !credentials || localKeyFailure === credentials.roomId) return;
    let cancelled = false;
    const generation = roomConnectionGeneration.current;
    void ensureLocalCredentials(credentials).then((upgraded) => {
      if (!cancelled && generation === roomConnectionGeneration.current)
        setCredentials((current) => current === credentials ? upgraded : current);
    }).catch(() => {
      if (!cancelled && generation === roomConnectionGeneration.current) {
        setLocalKeyFailure(credentials.roomId);
        notify("The direct window link could not start. Live relay synchronization is still available.");
      }
    });
    return () => { cancelled = true; };
  }, [credentials, needsLocalKey, localKeyFailure, notify]);
  useEffect(() => {
    if (!credentials) return;
    let cancelled = false;
    void getNetworkUrls(credentials)
      .then((result) => {
        if (!cancelled) {
          setNetworkUrls(result.urls);
          setLanEnabled(result.lanEnabled);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNetworkUrls([]);
          setLanEnabled(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [credentials]);
  useEffect(() => {
    if (!credentials || (needsLocalKey && localKeyFailure !== credentials.roomId)) return;
    let cancelled = false;
    const t = createTransport(
      credentials,
      role,
      role === "operator"
        ? "Operator"
        : role === "talent"
          ? "Talent display"
          : "Phone remote",
    );
    transport.current = t;
    const offStatus = t.onStatus((nextStatus) => {
      setStatus(nextStatus);
      if (!shouldRecoverCachedRoom({
        role,
        explicitRoute: route.explicitRoute,
        credentials,
        cached: readCachedRoom(),
        status: nextStatus,
        attempted: roomRecoveryAttempted.current,
      })) return;
      // Mark the one allowed attempt before any async work or another status
      // emission. Shared URLs and operator-conflict denials never reach here.
      roomRecoveryAttempted.current = true;
      const generation = ++roomConnectionGeneration.current;
      applyCommand({ type: "pause" });
      stopSpeech();
      rehearsalStop.current();
      try { sessionStorage.removeItem(ROOM); } catch {}
      notify("The live session expired. Playback is paused while a fresh room is prepared.");
      void createRoom(credentials.relayUrl)
        .then((fresh) => {
          if (cancelled || generation !== roomConnectionGeneration.current) return;
          setCredentials(fresh);
          notify("Live session recovered. Your script and position were kept, and playback is paused. Share the new display and phone links.");
        })
        .catch(() => {
          if (!cancelled && generation === roomConnectionGeneration.current) notify("Live recovery could not connect. Your script and position are preserved and paused. Use Start a new room to reconnect.");
        });
    }),
      offState = t.subscribe((s) => {
        if (role !== "operator") {
          setState(s);
          stateRef.current = s;
          setHasSnapshot(true);
        }
      }),
      offCommand = t.onCommand((cmd) => {
        if (role === "operator") applyCommand(cmd);
      });
    const offAgentRequest = t.onAgentRequest(request => agentExecuteRef.current(request));
    const offAgentDisconnect = t.onAgentDisconnect(clearAgentGrants);
    if (role === "operator") t.publish(stateRef.current);
    const pageHide = () => {
      if (role === "operator") {
        applyCommand({ type: "pause" });
        t.publish(stateRef.current);
      }
      t.close();
    };
    const pageShow = (event: PageTransitionEvent) => {
      if (event.persisted) location.reload();
    };
    window.addEventListener("pagehide", pageHide);
    window.addEventListener("pageshow", pageShow);
    return () => {
      cancelled = true;
      window.removeEventListener("pagehide", pageHide);
      window.removeEventListener("pageshow", pageShow);
      offStatus();
      offState();
      offCommand();
      offAgentRequest();
      offAgentDisconnect();
      clearAgentGrants();
      t.close();
      transport.current = null;
    };
  }, [credentials, role, needsLocalKey, localKeyFailure, applyCommand, route.explicitRoute, stopSpeech, notify, clearAgentGrants]);
  useEffect(() => {
    if (role === "operator") transport.current?.publish(state);
  }, [state, role]);
  useEffect(() => {
    if (role !== "operator") return;
    try {
      checkLibraryStorage();
      const serialized = JSON.stringify(scripts);
      localStorage.setItem(LIBRARY, serialized);
      libraryStorageBaseline.current = serialized;
      markStorage("library", true);
    } catch (error) {
      markStorage("library", false);
      notify(error instanceof WorkspaceStorageConflict ? error.message : "Browser storage is full. Export a backup before closing.");
    }
  }, [scripts, role, notify, markStorage, checkLibraryStorage]);
  useEffect(() => {
    if (role !== "operator") return;
    try {
      localStorage.setItem(SETTINGS, JSON.stringify(state.settings));
      markStorage("settings", true);
    } catch {
      markStorage("settings", false);
    }
  }, [state.settings, role, markStorage]);
  useEffect(() => {
    if (role !== "operator") return;
    let lastSaved: string | null = null;
    const persist = () => {
      try {
        const snapshot = JSON.stringify({
          scriptId: stateRef.current.script.id,
          wordIndex: stateRef.current.wordIndex,
          elapsed: stateRef.current.elapsed,
        });
        if (snapshot === lastSaved) return;
        localStorage.setItem(SESSION, snapshot);
        lastSaved = snapshot;
        markStorage("position", true);
      } catch {
        markStorage("position", false);
      }
    };
    const timer = setInterval(persist, 1500);
    window.addEventListener("pagehide", persist);
    return () => {
      clearInterval(timer);
      window.removeEventListener("pagehide", persist);
      persist();
    };
  }, [role, markStorage]);
  useEffect(() => {
    if (role !== "operator") return;
    let previous = performance.now(),
      fraction = 0,
      lastIndex = stateRef.current.wordIndex;
    const tick = setInterval(() => {
      const now = performance.now(),
        dt = (now - previous) / 1000;
      previous = now;
      const s = stateRef.current;
      if (lastIndex !== s.wordIndex) fraction = 0;
      const next = advanceSession(
        s,
        parsedRef.current,
        dt,
        fraction,
        speaking.current,
      );
      fraction = next.fraction;
      lastIndex = next.state.wordIndex;
      if (s.countdownUntil && !next.state.countdownUntil) {
        transcriptGuard.current.invalidate();
        engine.current?.resetTracking?.();
      }
      if (next.state !== s) mutate(() => next.state);
    }, 100);
    return () => clearInterval(tick);
  }, [role, mutate]);
  useEffect(() => {
    let sentinel: WakeLockSentinel | null = null,
      cancelled = false;
    const acquire = async () => {
      if (
        cancelled ||
        document.visibilityState !== "visible" ||
        !state.playing ||
        !state.settings.keepAwake ||
        !("wakeLock" in navigator) ||
        (sentinel && !sentinel.released)
      )
        return;
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) void lock.release();
        else sentinel = lock;
      } catch {}
    };
    void acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => {
      cancelled = true;
      void sentinel?.release();
      document.removeEventListener("visibilitychange", acquire);
    };
  }, [state.playing, state.settings.keepAwake]);
  useEffect(() => {
    if (role === "talent") return;
    const keys = (e: KeyboardEvent) => {
      // Foot pedals and held keys may repeat; transport toggles should fire once.
      if (e.repeat && (e.key === " " || e.key.toLowerCase() === "b")) return;
      const el = e.target as HTMLElement;
      if (
        el.closest(
          'input,textarea,select,[contenteditable="true"],[role="textbox"],[data-production-notes]',
        ) ||
        ((e.key === " " || e.key === "Enter") &&
          el.closest('button,a,summary,[role="button"]')) ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey
      )
        return;
      let cmd: ControlCommand | undefined;
      if (e.key === " ") cmd = { type: "toggle" };
      if (e.key === "ArrowLeft") cmd = { type: "nudge", words: -5 };
      if (e.key === "ArrowRight") cmd = { type: "nudge", words: 5 };
      if (e.key === "ArrowUp")
        cmd = { type: "speed", wpm: stateRef.current.settings.wpm + (stateRef.current.settings.speedStep ?? 10) };
      if (e.key === "ArrowDown")
        cmd = { type: "speed", wpm: stateRef.current.settings.wpm - (stateRef.current.settings.speedStep ?? 10) };
      if (e.key === "Home") cmd = { type: "seek", wordIndex: 0 };
      if (e.key === "End")
        cmd = { type: "seek", wordIndex: parsedRef.current.words.length - 1 };
      if (e.key.toLowerCase() === "b") cmd = { type: "blackout" };
      if (e.key === "Escape") cmd = { type: "pause" };
      if (cmd) {
        e.preventDefault();
        command(cmd);
      }
    };
    window.addEventListener("keydown", keys);
    return () => window.removeEventListener("keydown", keys);
  }, [command, role]);
  useEffect(() => {
    void registerOfflineApp((activate) => {
      activateUpdate.current = activate;
      setUpdateAvailable(true);
      notify(
        "An app update is ready. Apply it from Settings & tools → Recovery & updates after your take.",
      );
    });
  }, [notify]);
  const applyUpdate = useCallback(() => {
    if (stateRef.current.playing || stateRef.current.countdownUntil) {
      notify("Pause the current take before applying an app update.");
      return;
    }
    if (!activateUpdate.current) return;
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => location.reload(),
      { once: true },
    );
    activateUpdate.current();
  }, [notify]);
  useEffect(() => {
    speechMounted.current = true;
    return () => {
      speechMounted.current = false;
      speechGeneration.current++;
      speechRequest.current = null;
      engine.current?.stop();
      engine.current = null;
      audioAbort.current?.abort();
      modelAbort.current?.abort();
      clearTimeout(noticeTimeout.current);
    };
  }, []);
  const updateSettings = useCallback(
    (settings: Partial<PrompterSettings>) => {
      // Settings restores and remote commands may stop incompatible listening,
      // but only an explicit local selection or retry may open a microphone.
      command({ type: "settings", settings });
    },
    [command],
  );
  const updateScript = useCallback(
    (patch: Partial<CueScript>) => {
      if (role !== "operator") return;
      try { checkLibraryStorage(); }
      catch (error) {
        notify(error instanceof Error ? error.message : "Script changes could not be saved.");
        return;
      }
      const prev = stateRef.current.script;
      if (
        patch.html !== undefined &&
        patch.html !== prev.html &&
        Date.now() - (lastVersion.current[prev.id] || 0) > 60000
      ) {
        history.save(prev, "Before editing");
        lastVersion.current[prev.id] = Date.now();
      }
      const next = normalizeScript({
        ...prev,
        ...patch,
        html: patch.html !== undefined ? sanitizeScript(patch.html) : prev.html,
        updatedAt: Date.now(),
      });
      if (!next) {
        notify(
          "Script exceeds the live-display limit. Split it into a rundown, or export the original file.",
        );
        return;
      }
      const nextScripts = workspaceRef.current.scripts.map((x) => x.id === next.id ? next : x);
      workspaceRef.current = { ...workspaceRef.current, scripts: nextScripts };
      setScripts(nextScripts);
      mutate((s) => ({
        ...s,
        script: next,
        wordIndex: Math.min(
          s.wordIndex,
          Math.max(0, parseScript(next.html).words.length - 1),
        ),
      }));
    },
    [role, mutate, history.save, notify, checkLibraryStorage],
  );
  const saveVersion = useCallback(
    (label?: string) => {
      history.save(stateRef.current.script, label || "Saved version");
      notify("Script version saved.");
    },
    [history.save, notify],
  );
  const restoreVersion = useCallback(
    (id: string) => {
      const version = history.versions.find((v) => v.id === id);
      if (!version) return;
      history.save(stateRef.current.script, "Before restoring a version");
      updateScript({ ...version.script, id: stateRef.current.script.id });
      command({ type: "reset" });
      notify("Script version restored.");
    },
    [history, updateScript, command, notify],
  );
  const restoreTrash = useCallback(
    (id: string) => {
      if (role !== "operator") return;
      const current = workspaceRef.current;
      if (current.scripts.length >= 300) {
        notify(
          "The library can hold 300 scripts. Make room before restoring this script.",
        );
        return;
      }
      const script = current.trash.find((x) => x.id === id);
      if (!script) return;
      const restored = {
        ...script,
        id: current.scripts.some((x) => x.id === id) ? crypto.randomUUID() : id,
        updatedAt: Date.now(),
      };
      const nextTrash = current.trash.filter((x) => x.id !== id);
      if (!commitScriptLibrary([restored, ...current.scripts], current.projectFoldersState, { "cue-studio-trash-v1": nextTrash })) return;
      workspaceRef.current = { ...workspaceRef.current, trash: nextTrash };
      setTrash(nextTrash);
      notify("Script restored to your library.");
    },
    [role, notify, commitScriptLibrary],
  );
  const selectScript = useCallback(
    (id: string) => {
      const script = scripts.find((x) => x.id === id);
      if (script && role === "operator") {
        stopSpeech();
        mutate((s) => ({
          ...initialState(script, s.settings),
          revision: s.revision,
        }));
      }
    },
    [scripts, role, stopSpeech, mutate],
  );
  const createScript = useCallback((folderId: string | null = null) => {
    if (role !== "operator") return;
    const current = workspaceRef.current;
    if (current.scripts.length >= 300) {
      notify(
        "Your library has reached 300 scripts. Export a backup and remove an unused script before adding another.",
      );
      return;
    }
    const script: CueScript = {
      id: crypto.randomUUID(),
      title: "Untitled script",
      html: "<p>Start your next script here.</p>",
      notes: "",
      tags: [],
      updatedAt: Date.now(),
    };
    let folders: ProjectFoldersState;
    try { folders = moveProjectScript(current.projectFoldersState, script.id, folderId); }
    catch (error) { notify(error instanceof Error ? error.message : "Choose a project for this script."); return; }
    if (!commitScriptLibrary([script, ...current.scripts], folders)) return;
    stopSpeech();
    mutate((s) => ({
      ...initialState(script, s.settings),
      revision: s.revision,
    }));
  }, [role, notify, mutate, stopSpeech, commitScriptLibrary]);
  const duplicateScript = useCallback(
    (id: string) => {
      if (role !== "operator") return;
      const current = workspaceRef.current;
      if (current.scripts.length >= 300) {
        notify(
          "Your library has reached 300 scripts. Export a backup and remove an unused script before duplicating.",
        );
        return;
      }
      const source = current.scripts.find((x) => x.id === id);
      if (!source) return;
      const script = {
        ...source,
        id: crypto.randomUUID(),
        title: source.title + " — copy",
        updatedAt: Date.now(),
      };
      const folderId = current.projectFoldersState.folders.find(folder => folder.scriptIds.includes(id))?.id || null;
      if (!commitScriptLibrary([script, ...current.scripts], moveProjectScript(current.projectFoldersState, script.id, folderId))) return;
      stopSpeech();
      mutate((s) => ({
        ...initialState(script, s.settings),
        revision: s.revision,
      }));
    },
    [role, notify, mutate, stopSpeech, commitScriptLibrary],
  );
  const deleteScript = useCallback(
    (id: string) => {
      if (role !== "operator") return;
      const current = workspaceRef.current;
      if (current.scripts.length === 1) {
        notify("Keep at least one script in your library.");
        return;
      }
      const deleted = current.scripts.find((x) => x.id === id);
      if (!deleted) return;
      const nextTrash = [deleted, ...current.trash.filter((x) => x.id !== id)].slice(0, 100);
      const remaining = current.scripts.filter((x) => x.id !== id);
      if (!commitScriptLibrary(remaining, current.projectFoldersState, { "cue-studio-trash-v1": nextTrash })) return;
      workspaceRef.current = { ...workspaceRef.current, trash: nextTrash };
      setTrash(nextTrash);
      notify("Script moved to recovery. Restore it from Settings & tools → Recovery & updates.");
      if (stateRef.current.script.id === id) {
        stopSpeech();
        mutate((s) => ({
          ...initialState(remaining[0], s.settings),
          revision: s.revision,
        }));
      }
    },
    [role, mutate, notify, stopSpeech, commitScriptLibrary],
  );
  const importScripts = useCallback(
    async (files: File[], folderId: string | null = null) => {
      if (role !== "operator") return;
      if (fileImportBusy.current) {
        notify(
          "Another import is still being read. Wait for it to finish, then try again.",
        );
        return;
      }
      fileImportBusy.current = true;
      try {
        if (workspaceRef.current.scripts.length + files.length > 300)
          throw new Error(
            "The library can hold 300 scripts. Export a backup and make room before importing.",
          );
        const added: CueScript[] = [];
        for (const file of files) added.push(await importScriptFile(file));
        if (workspaceRef.current.scripts.length + added.length > 300)
          throw new Error(
            "The library changed while reading these files and would exceed 300 scripts. Your current work was kept; make room and try again.",
          );
        if (added.length) {
          let folders = workspaceRef.current.projectFoldersState;
          for (const script of added) folders = moveProjectScript(folders, script.id, folderId);
          if (!commitScriptLibrary([...added, ...workspaceRef.current.scripts], folders)) return;
          stopSpeech();
          mutate((s) => ({
            ...initialState(added[0], s.settings),
            revision: s.revision,
          }));
          notify(
            `Imported ${added.length} script${added.length === 1 ? "" : "s"}.`,
          );
        }
      } catch (e) {
        notify(
          e instanceof Error
            ? e.message
            : "Import failed. Try plain text, HTML, Markdown, DOCX, subtitles or JSON.",
        );
      } finally {
        fileImportBusy.current = false;
      }
    },
    [role, mutate, stopSpeech, notify, commitScriptLibrary],
  );
  const exportScript = useCallback((format: "html" | "txt" | "json", scriptId?: string) => {
    const current = stateRef.current.script;
    const s = !scriptId || scriptId === current.id
      ? current : workspaceRef.current.scripts.find((script) => script.id === scriptId);
    if (!s) { notify("This script is no longer in your library."); return; }
    const name =
      s.title.replace(/[^\p{L}\p{N}\s_-]/gu, "").trim().slice(0, 80) || "script";
    download(
      name + "." + format,
      format === "html"
        ? s.html
        : format === "txt"
          ? parseScript(s.html).text
          : JSON.stringify(s, null, 2),
      format === "html"
        ? "text/html"
        : format === "json"
          ? "application/json"
          : "text/plain",
    );
  }, [notify]);
  const exportBatch = useCallback(async (ids: string[], format: BatchExportFormat, options: { signal: AbortSignal; onProgress: (done: number, total: number) => void }) => {
    if (role !== "operator") throw new Error("Export scripts from the operator workspace.");
    const current = stateRef.current.script;
    const requested = new Set(ids);
    const selected = workspaceRef.current.scripts.filter(script => requested.has(script.id))
      .map(script => ({ ...(script.id === current.id ? current : script), tags: [...script.tags] }));
    if (!selected.length) throw new Error("Select at least one script to export.");
    if (selected.length !== requested.size) throw new Error("A selected script is no longer in your library. Check your selection and try again.");
    const { createBatchExport } = await import("./studio/batch-export");
    const result = await createBatchExport(selected, format, options);
    if (options.signal.aborted) throw new DOMException("Export canceled.", "AbortError");
    const url = URL.createObjectURL(result.blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = result.name;
    anchor.hidden = true;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    notify(`${selected.length} ${selected.length === 1 ? "script" : "scripts"} exported as ${format === "docx" ? "Word" : "plain text"}${selected.length > 1 ? " in a ZIP" : ""}.`);
  }, [role, notify]);
  const exportBackup = useCallback(
    () =>
      download(
        "cue-studio-backup-" + new Date().toISOString().slice(0, 10) + ".json",
        JSON.stringify(
          {
            format: "cue-studio",
            version: 2,
            exportedAt: new Date().toISOString(),
            scripts: workspaceRef.current.scripts,
            projectFolders: workspaceRef.current.projectFoldersState,
            settings: stateRef.current.settings,
            rundown: workspaceRef.current.rundown,
            versions: workspaceRef.current.versions,
            trash: workspaceRef.current.trash,
            takes: workspaceRef.current.takes,
            launchpad: readLaunchpad(),
            productionNotes: productionNotes.getDocument(),
          },
          null,
          2,
        ),
        "application/json",
      ),
    [scripts, rundown, history.versions, trash, journal.takes, productionNotes.document],
  );
  const importBackup = useCallback(
    async (file: File) => {
      if (role !== "operator") return;
      if (fileImportBusy.current) {
        notify(
          "Another import is still being read. Wait for it to finish, then try again.",
        );
        return;
      }
      fileImportBusy.current = true;
      const workspaceBeforeRead = workspaceRef.current;
      const scriptBeforeRead = stateRef.current.script;
      const settingsBeforeRead = stateRef.current.settings;
      const launchpadBeforeRead = JSON.stringify(readLaunchpad());
      const notesBeforeRead = productionNotes.getDocument();
      try {
        if (file.size > 20 * 1024 * 1024)
          throw new Error("Backup must be smaller than 20 MB.");
        const savedProjectsBeforeRead = localStorage.getItem(PROJECT_FOLDERS_STORAGE);
        const data = JSON.parse(await file.text());
        if (
          workspaceRef.current !== workspaceBeforeRead ||
          stateRef.current.script !== scriptBeforeRead ||
          stateRef.current.settings !== settingsBeforeRead ||
          localStorage.getItem(PROJECT_FOLDERS_STORAGE) !== savedProjectsBeforeRead ||
          JSON.stringify(readLaunchpad()) !== launchpadBeforeRead ||
          productionNotes.getDocument() !== notesBeforeRead
        )
          throw new Error(
            "Workspace changed while reading this backup. Try restoring it again; your current work was kept.",
          );
        if (
          data.format !== "cue-studio" ||
          !Array.isArray(data.scripts) ||
          !data.scripts.length
        )
          throw new Error("This is not a Cue Studio backup.");
        // Older backups have no launchpad and must not reset the current keys.
        // Reject an unsupported container before modifying any workspace data;
        // normalize individual fields using the same rules as the editor.
        const hasLaunchpad = Object.hasOwn(data, "launchpad");
        if (hasLaunchpad && (!data.launchpad || typeof data.launchpad !== "object" ||
          Array.isArray(data.launchpad) || data.launchpad.version !== 1 || !Array.isArray(data.launchpad.keys)))
          throw new Error("This backup contains an unsupported launchpad configuration. Your workspace was kept.");
        const launchpad = hasLaunchpad ? normalizeLaunchpad(data.launchpad) : null;
        const hasProjectFolders = Object.hasOwn(data, "projectFolders");
        if (hasProjectFolders && !validProjectFoldersBackup(data.projectFolders))
          throw new Error("This backup contains invalid project folders. Your workspace was kept.");
        if (data.productionNotes != null && !validProductionDocument(data.productionNotes))
          throw new Error("This backup contains invalid editor notes. Your workspace was kept.");
        // A fresh workspace includes a sample, so additive restoration alone
        // cannot restore a valid backup containing the full 300-script limit.
        // Only discard that sample when no user content or take depends on it.
        const {
          scripts: currentScripts,
          rundown: currentRundown,
          versions: currentVersions,
          trash: currentTrash,
          takes: currentTakes,
          projectFoldersState: currentProjectFolders,
        } = workspaceBeforeRead;
        const starter = currentScripts[0];
        const replaceStarter =
          currentScripts.length === 1 &&
          starter.id === demoScript.id &&
          starter.title === demoScript.title &&
          sanitizeScript(starter.html) === sanitizeScript(demoScript.html) &&
          starter.notes === demoScript.notes &&
          JSON.stringify(starter.tags) === JSON.stringify(demoScript.tags) &&
          !currentRundown.length &&
          !currentVersions.length &&
          !currentTrash.length &&
          !currentTakes.length &&
          !currentProjectFolders.folders.some(folder => folder.scriptIds.includes(starter.id)) &&
          !stateRef.current.playing &&
          !stateRef.current.countdownUntil;
        if (
          (replaceStarter ? 0 : currentScripts.length) + data.scripts.length >
          300
        )
          throw new Error(
            "This backup would exceed the 300-script library limit. Existing scripts were kept; make room or import into another browser profile.",
          );
        const idMap = new Map<string, string>();
        const incomingIds = new Set<string>();
        const incoming: CueScript[] = data.scripts
          .slice(0, 300)
          .flatMap((x: unknown) => {
            const script = normalizeScript(x, true);
            if (!script) return [];
            if (
              x &&
              typeof x === "object" &&
              typeof (x as CueScript).id === "string"
            ) {
              if (incomingIds.has((x as CueScript).id)) throw new Error("This backup contains duplicate script IDs. Your workspace was kept.");
              incomingIds.add((x as CueScript).id);
              idMap.set((x as CueScript).id, script.id);
            }
            return [script];
          });
        if (!incoming.length)
          throw new Error("Backup contains no valid scripts.");
        const takeIds = new Map<string,string>();
        if (Array.isArray(data.takes)) for (const take of data.takes.slice(0,500))
          if (typeof take?.id === 'string') takeIds.set(take.id, crypto.randomUUID());
        const restoredNotes = data.productionNotes ? productionNotes.prepareRestore(data.productionNotes, idMap, takeIds) : null;
        const nextScripts = replaceStarter ? incoming : [...incoming, ...currentScripts];
        const nextFolders = restoreProjectFolders(currentProjectFolders, hasProjectFolders ? data.projectFolders : undefined, idMap, nextScripts.map(script => script.id));
        const nextRundown = Array.isArray(data.rundown)
          ? [...currentRundown, ...data.rundown.slice(0, 1000).flatMap((id: unknown) => typeof id === "string" && idMap.has(id) ? [idMap.get(id)!] : [])].slice(0, 1000)
          : currentRundown;
        const nextVersions = prepareRestoredVersions(currentVersions, data.versions, idMap);
        const nextTrash = Array.isArray(data.trash)
          ? [...data.trash.slice(0, 100).map((item: unknown) => normalizeScript(item, true)).filter((item: CueScript | null): item is CueScript => !!item), ...currentTrash].slice(0, 100)
          : currentTrash;
        const nextTakes = data.takes ? prepareRestoredTakes(currentTakes, data.takes, idMap, takeIds) : currentTakes;
        const nextSettings = data.settings ? normalizeSettings(data.settings, stateRef.current.settings) : stateRef.current.settings;
        // Stage every backup area before publishing any change. Quota failures roll
        // back previous storage values and leave the live workspace untouched.
        const values: Record<string, unknown> = {
          [SETTINGS]: nextSettings,
          "cue-rundown": nextRundown,
          "cue-studio-versions-v1": nextVersions,
          "cue-studio-trash-v1": nextTrash,
          "cue-studio-takes-v1": nextTakes,
        };
        if (restoredNotes) values[NOTES_STORAGE_PREFIX + restoredNotes.projectId] = restoredNotes;
        if (launchpad) values[LAUNCHPAD_STORAGE] = launchpad;
        if (replaceStarter) values[SESSION] = { scriptId: incoming[0].id, wordIndex: 0, elapsed: 0 };
        if (!commitScriptLibrary(nextScripts, nextFolders, values)) return;
        workspaceRef.current = { ...workspaceRef.current, rundown: nextRundown, versions: nextVersions, trash: nextTrash, takes: nextTakes };
        if (launchpad) window.dispatchEvent(new CustomEvent(LAUNCHPAD_CHANGED));
        if (restoredNotes) productionNotes.restoreDocument(restoredNotes, { persisted: true });
        setRundown(nextRundown);
        history.replace(nextVersions);
        setTrash(nextTrash);
        journal.replace(nextTakes);
        if (replaceStarter) {
          stopSpeech();
          mutate((s) => ({
            ...initialState(
              incoming[0],
              nextSettings,
            ),
            revision: s.revision,
          }));
        } else if (data.settings)
          command({
            type: "settings",
            settings: nextSettings,
          });
        notify(
          `Restored ${incoming.length} scripts, rundown and workspace data. ${replaceStarter ? "The untouched welcome sample was replaced." : "Existing scripts were preserved."}`,
        );
      } catch (e) {
        notify(e instanceof Error ? e.message : "Backup could not be read.");
      } finally {
        fileImportBusy.current = false;
      }
    },
    [role, notify, command, mutate, stopSpeech, history.replace, journal.replace, commitScriptLibrary],
  );
  const connectRelay = useCallback(
    async (url: string) => {
      if (role !== "operator" || roomConnectionBusy.current) return;
      roomConnectionBusy.current = true;
      ++roomConnectionGeneration.current;
      applyCommand({ type: "pause" });
      stopSpeech();
      rehearsalStop.current();
      try {
        const c = await createRoom(url || undefined);
        setCredentials(c);
        notify(
          c.relayUrl
            ? "Live room ready. Your script and position were kept, and playback is paused. Share the new display and phone links."
            : "Same-computer session ready. Your script and position were kept, and playback is paused. Use the local server for phone connections.",
        );
      } catch (e) {
        notify(e instanceof Error ? e.message : "Could not connect to relay.");
      } finally {
        roomConnectionBusy.current = false;
      }
    },
    [role, notify, applyCommand, stopSpeech],
  );
  const getViewUrl = useCallback(
    (viewRole: ViewRole, origin?: string) =>
      credentials ? createViewUrl(credentials, viewRole, origin) : "",
    [credentials],
  );
  const openOutput = useCallback(() => {
    const url = getViewUrl("talent");
    if (!url) {
      notify("The display session is still preparing. Try again in a moment.");
      return;
    }
    const output = window.open(
      url,
      "cue-talent-" + credentials?.roomId,
      `popup,width=${stateRef.current.settings.outputWidth},height=${stateRef.current.settings.outputHeight}`,
    );
    if (!output)
      notify("Allow pop-up windows, then open the talent display again.");
  }, [getViewUrl, credentials, notify]);
  const copyText = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        notify("Copied.");
      } catch {
        notify(
          "Clipboard access is unavailable. Select the link and copy it manually.",
        );
      }
    },
    [notify],
  );
  const consumeTranscript = useCallback(
    (text: string, final = true, id?: string) => {
      setSpeechStatus((prev) => ({ ...prev, transcript: text }));
      const s = stateRef.current;
      if (
        !s.playing ||
        s.countdownUntil ||
        s.settings.mode !== "voice" ||
        performance.now() < overrideUntil.current
      )
        return;
      const result = matcher.current!.match(text, {
        final,
        session: id,
        confidenceThreshold: s.settings.confidenceThreshold,
      });
      setSpeechStatus((prev) => ({ ...prev, confidence: result.confidence }));
      if (result.matched && result.confidence >= s.settings.confidenceThreshold)
        mutate((x) => ({ ...x, wordIndex: result.wordIndex, wordProgress: 0 }));
    },
    [mutate],
  );
  const feedTranscript = useCallback(
    (text: string, final = true, id?: string) => {
      if (role !== "operator") return;
      consumeTranscript(text.slice(0, 100000), final, id);
    },
    [role, consumeTranscript],
  );
  const loadMicrophones = useCallback(async () => {
    try {
      const { listMicrophones } = await import("./speech/engines");
      setMicrophones(await listMicrophones());
    } catch (e) {
      notify(e instanceof Error ? e.message : "Microphone list unavailable.");
    }
  }, [notify]);
  const startSpeech = useCallback(async () => {
    if (role !== "operator" || !speechMounted.current) return;
    if (modelAbort.current || audioAbort.current) {
      const error = "Finish or cancel the offline preparation or audio transcription, then retry listening.";
      setSpeechStatus({ active: false, status: "Listening is waiting", error });
      notify(error);
      return;
    }
    const selected = stateRef.current.settings;
    const mode = selected.mode === "sound" ? "sound" : "voice";
    const speechEngine = mode === "sound" ? "sound" : selected.speechEngine;
    const key = JSON.stringify([mode, speechEngine, selected.language, microphoneId, speechModel, selected.streamingModel]);
    if (speechRequest.current?.key === key) return speechRequest.current.promise;
    rehearsalStop.current();
    stopSpeech();
    if (selected.mode !== mode) command({ type: "settings", settings: { mode } });
    const gen = ++speechGeneration.current;
    const current = stateRef.current.settings;
    const previous = speechStartBarrier.current;
    setSpeechStatus({
      active: false,
      preparing: true,
      status: previous ? "Waiting for the previous microphone request" : "Preparing speech engine",
      engine: speechEngine,
      progress: 0,
    });
    const request = (async () => {
      try {
        if (previous) await previous;
        if (gen !== speechGeneration.current) return;
        if (speechEngine === "external") {
          setSpeechStatus({ active: true, status: "External transcript ready", engine: "external" });
          return;
        }
        const module = await import("./speech/engines");
        if (gen !== speechGeneration.current) return;
        const listening = () => {
          if (gen !== speechGeneration.current) return;
          setSpeechStatus((prev) => ({
            ...prev,
            active: true,
            preparing: false,
            status: mode === "sound" ? "Sound gate listening" : "Listening",
            progress: 100,
          }));
        };
        const callbacks = {
          onListeningChange: (active: boolean) => {
            if (gen !== speechGeneration.current) return;
            if (active) listening();
            else setSpeechStatus((prev) => ({ ...prev, active: false, preparing: true, status: "Browser speech reconnecting", progress: undefined }));
          },
          onTranscript: ({ text, final, id, stamp }: SpeechTranscript) => {
            if (gen === speechGeneration.current && (!stamp || transcriptGuard.current.accept(stamp).accepted)) consumeTranscript(text, final, id);
          },
          onStatus: (text: string) => {
            if (gen === speechGeneration.current)
              setSpeechStatus((prev) => ({ ...prev, status: text }));
          },
          onError: (error: string) => {
            if (gen !== speechGeneration.current) return;
            // Invalidate start() as well: some engines report an error before
            // their start promise settles. Its late resolution must not revive it.
            stopSpeech();
            setSpeechStatus({ active: false, preparing: false, engine: speechEngine, error, status: "Speech stopped" });
          },
          onProgress: ({ progress }: { progress?: number }) => {
            if (gen === speechGeneration.current)
              setSpeechStatus((prev) => ({ ...prev, progress: progress ?? 0 }));
          },
          onLevel: (_level: number, active: boolean) => {
            if (gen === speechGeneration.current) speaking.current = active;
          },
        };
        const SpeechClass = mode === "sound" ? module.SoundGate
          : speechEngine === "streaming" ? (await import("./speech/streaming-engine")).StreamingSpeechEngine
          : speechEngine === "local" ? module.LocalSpeechEngine : module.BrowserSpeechEngine;
        if (gen !== speechGeneration.current) return;
        const next = new SpeechClass(callbacks);
        engine.current = next;
        await next.start({ language: current.language, deviceId: microphoneId || undefined, model: speechModel, streamingModel: current.streamingModel, getTrackingEpoch: () => transcriptGuard.current.epoch });
        if (gen !== speechGeneration.current) {
          next.stop();
          return;
        }
        // Browser start() submits a permission request. Only its actual onstart
        // event confirms listening; local/sound resolve after capture is ready.
        if (speechEngine !== "browser") listening();
        void loadMicrophones();
      } catch (error) {
        if (gen !== speechGeneration.current) return;
        stopSpeech();
        setSpeechStatus({
          active: false,
          status: "Speech unavailable",
          engine: speechEngine,
          error: error instanceof Error ? error.message : String(error),
        });
        notify("Speech could not start. Manual and fixed-speed controls remain available.");
      }
    })();
    speechStartBarrier.current = request;
    speechRequest.current = { key, promise: request };
    try { await request; }
    finally {
      if (speechStartBarrier.current === request) speechStartBarrier.current = null;
    }
  }, [role, stopSpeech, command, consumeTranscript, microphoneId, speechModel, loadMicrophones, notify]);
  const selectScrollMode = useCallback(async (mode: ScrollMode) => {
    if (!speechMounted.current) return;
    command({ type: "settings", settings: { mode } });
    if (role !== "operator") return;
    if (mode === "voice" || mode === "sound") await startSpeech();
    else stopSpeech();
  }, [command, role, startSpeech, stopSpeech]);
  const cancelAudio = useCallback(() => {
    audioAbort.current?.abort();
    audioAbort.current = null;
    setAudioStatus((prev) => ({ ...prev, busy: false }));
  }, []);
  const transcribeFile = useCallback(
    async (file: File) => {
      if (role !== "operator") return;
      if (
        stateRef.current.playing ||
        stateRef.current.countdownUntil ||
        engine.current ||
        speechStartBarrier.current ||
        modelAbort.current
      ) {
        notify(
          "Pause the take and stop listening or model preparation before transcribing an audio file.",
        );
        return;
      }
      cancelAudio();
      const abort = new AbortController();
      audioAbort.current = abort;
      setAudioStatus({ busy: true, progress: 0 });
      try {
        const { transcribeAudio } = await import("./speech/engines");
        const result = await transcribeAudio(
          file,
          {
            onProgress: ({ progress }) => {
              if (!abort.signal.aborted)
                setAudioStatus((prev) => ({
                  ...prev,
                  progress: progress ?? 0,
                }));
            },
          },
          {
            language: stateRef.current.settings.language,
            model: speechModel,
            signal: abort.signal,
          },
        );
        if (abort.signal.aborted) return;
        setAudioStatus({ busy: false, progress: 100, transcript: result.text });
      } catch (e) {
        if (!abort.signal.aborted)
          setAudioStatus({
            busy: false,
            error: e instanceof Error ? e.message : String(e),
          });
      } finally {
        if (audioAbort.current === abort) audioAbort.current = null;
      }
    },
    [role, cancelAudio, speechModel, notify],
  );
  const refreshModel = useCallback(async () => {
    if (role !== "operator") return;
    const generation = ++modelReadGeneration.current;
    const selected = stateRef.current.settings;
    const stillSelected = () => generation === modelReadGeneration.current &&
      selected.speechEngine === stateRef.current.settings.speechEngine &&
      selected.streamingModel === stateRef.current.settings.streamingModel && !modelAbort.current;
    setModelStatus((prev) => ({ ...prev, ready: false }));
    try {
      if (selected.speechEngine === "streaming") {
        const { streamingModelReady } = await import("./speech/streaming-engine");
        const ready = await streamingModelReady(selected.streamingModel);
        if (stillSelected()) setModelStatus((prev) => ({ ...prev, ready }));
        return;
      }
      const { getSpeechModelCacheInfo } = await import("./speech/engines");
      const info = await getSpeechModelCacheInfo();
      if (stillSelected()) setModelStatus((prev) => ({ ...prev, ready: info[speechModel] }));
    } catch {}
  }, [role, speechModel, state.settings.speechEngine, state.settings.streamingModel]);
  const cancelModel = useCallback(() => {
    modelReadGeneration.current++;
    modelAbort.current?.abort();
    modelAbort.current = null;
    setModelStatus((prev) => ({ ...prev, busy: false }));
  }, []);
  useEffect(() => {
    cancelModel();
    void refreshModel();
  }, [refreshModel, cancelModel, state.settings.language]);
  const prepareModel = useCallback(async () => {
    if (
      role !== "operator" ||
      stateRef.current.playing ||
      stateRef.current.countdownUntil ||
      engine.current ||
      speechStartBarrier.current ||
      audioAbort.current
    )
      return;
    cancelModel();
    const abort = new AbortController();
    const selected = stateRef.current.settings;
    modelAbort.current = abort;
    setModelStatus({ busy: true, progress: 0, ready: false });
    try {
      const callbacks = { onProgress: ({ progress }: { progress?: number }) => {
        if (!abort.signal.aborted) setModelStatus((prev) => ({ ...prev, progress: progress ?? 0 }));
      } };
      if (selected.speechEngine === "streaming") {
        const { prepareStreamingModel } = await import("./speech/streaming-engine");
        await prepareStreamingModel(callbacks, { model: selected.streamingModel, signal: abort.signal });
      } else {
        const { prepareLocalModel } = await import("./speech/engines");
        await prepareLocalModel(callbacks, { model: speechModel, signal: abort.signal });
      }
      if (!abort.signal.aborted) {
        setModelStatus({ busy: false, progress: 100, ready: true });
        notify("Offline speech model prepared. No microphone was opened.");
      }
    } catch (e) {
      if (!abort.signal.aborted)
        setModelStatus({
          busy: false,
          progress: 0,
          ready: false,
          error: e instanceof Error ? e.message : String(e),
        });
    } finally {
      if (modelAbort.current === abort) modelAbort.current = null;
    }
  }, [role, cancelModel, speechModel, notify]);
  const clearModels = useCallback(async () => {
    cancelModel();
    stopSpeech();
    cancelAudio();
    try {
      const { clearSpeechModelCache } = await import("./speech/engines");
      await clearSpeechModelCache();
      if (typeof caches !== "undefined") await caches.delete("moonshine-models-v1");
      setModelStatus({ busy: false, progress: 0, ready: false });
      notify(
        "Downloaded speech models removed. You can prepare them again at any time.",
      );
    } catch (e) {
      notify(
        e instanceof Error ? e.message : "Could not remove cached models.",
      );
    }
  }, [cancelModel, stopSpeech, cancelAudio, notify]);
  const beforeRehearsal = useCallback(() => {
    stopSpeech();
    command({ type: "pause" });
  }, [stopSpeech, command]);
  const rehearsal = useRehearsal(beforeRehearsal, state.script);
  const recording = useRecorder();
  rehearsalStop.current = rehearsal.stop;
  agentHostRef.current = {
    get: () => ({ state: stateRef.current, scripts: workspaceRef.current.scripts.map(script => script.id === stateRef.current.script.id ? stateRef.current.script : script), rundown: workspaceRef.current.rundown, trash: workspaceRef.current.trash, status, storageHealthy, editingLocked: agentEditingLocked.current, notes: productionNotes.getDocument(), launchpad: readLaunchpad() }),
    grants: () => approvedAgents.current,
    commit: change => {
      if (change.scripts) {
        try { checkProjectStorage(); checkLibraryStorage(); }
        catch (error) { if (error instanceof WorkspaceStorageConflict) notify(error.message); throw error; }
      }
      const values: Record<string, unknown> = {};
      const nextFolders = change.scripts
        ? normalizeProjectFolders(workspaceRef.current.projectFoldersState, change.scripts.map(script => script.id))
        : workspaceRef.current.projectFoldersState;
      if (change.scripts) {
        values[LIBRARY] = change.scripts;
        values[PROJECT_FOLDERS_STORAGE] = nextFolders;
      }
      if (change.rundown) values["cue-rundown"] = change.rundown;
      if (change.trash) values["cue-studio-trash-v1"] = change.trash;
      if (change.state) values[SESSION] = { scriptId: change.state.script.id, wordIndex: change.state.wordIndex, elapsed: change.state.elapsed };
      persistAgentValues(localStorage, values);
      if (change.scripts) {
        projectStorageBaseline.current = JSON.stringify(nextFolders);
        libraryStorageBaseline.current = JSON.stringify(change.scripts);
      }
      workspaceRef.current = { ...workspaceRef.current, ...(change.scripts ? { scripts: change.scripts, projectFoldersState: nextFolders } : {}), ...(change.rundown ? { rundown: change.rundown } : {}), ...(change.trash ? { trash: change.trash } : {}) };
      if (change.scripts) {
        setScripts(change.scripts);
        setProjectFoldersState(nextFolders);
      }
      if (change.rundown) setRundown(change.rundown);
      if (change.trash) setTrash(change.trash);
      if (change.state) {
        const old = stateRef.current;
        if (change.stopListening || change.state.script.id !== old.script.id) stopSpeech();
        if (change.stopListening || change.state.script.html !== old.script.html || change.state.script.id !== old.script.id) {
          parsedRef.current = parseScript(change.state.script.html);
          matcher.current = new SpeechMatcher(parsedRef.current.words);
          matcher.current.setPosition(change.state.wordIndex);
          transcriptGuard.current.invalidate();
          engine.current?.resetTracking?.();
        }
        mutate(() => change.state!);
      }
    },
    saveVersion: (script, label) => history.save(script, label),
    settings: patch => {
      const next = normalizeSettings(patch, stateRef.current.settings);
      persistAgentValues(localStorage, { [SETTINGS]: next });
      applyCommand({ type: "settings", settings: patch });
    },
    command: cmd => {
      const next = applySessionCommand(stateRef.current, cmd, parsedRef.current);
      const values: Record<string, unknown> = { [SESSION]: { scriptId: next.script.id, wordIndex: next.wordIndex, elapsed: next.elapsed } };
      if (next.settings !== stateRef.current.settings) values[SETTINGS] = next.settings;
      persistAgentValues(localStorage, values);
      applyCommand(cmd);
    },
    notes: document => { saveProductionDocument(document); productionNotes.restoreDocument(document); },
    feedTranscript,
  };
  if (!agentExecutor.current) {
    // The queue and retry ledger survive renders; each call reads current hook state.
    agentExecutor.current = createAgentExecutor({
      get: () => agentHostRef.current!.get(), grants: () => agentHostRef.current!.grants(),
      commit: value => agentHostRef.current!.commit(value), saveVersion: (script, label) => agentHostRef.current!.saveVersion(script, label),
      settings: patch => agentHostRef.current!.settings(patch), command: cmd => agentHostRef.current!.command(cmd),
      notes: doc => agentHostRef.current!.notes(doc), feedTranscript: (text, final, id) => agentHostRef.current!.feedTranscript(text, final, id),
    });
  }
  agentExecuteRef.current = async request => {
    if (role !== "operator") return { ok: false, error: { code: "operator_required", message: "This window cannot execute agent actions." } };
    const result = await agentExecutor.current!(request);
    setAgentActivity(items => [{ operation: request.operation, at: Date.now(), ok: result.ok }, ...items].slice(0, 12));
    return result;
  };
  return {
    agentGrants,
    agentActivity,
    agentManage,
    setAgentEditingLocked,
    state,
    parsed,
    scripts,
    projectFolders: projectFoldersState.folders,
    createProjectFolder,
    renameProjectFolder,
    deleteProjectFolder,
    moveScriptToFolder,
    rundown,
    setRundown,
    modelStatus,
    prepareModel,
    cancelModel,
    clearModels,
    updateAvailable,
    applyUpdate,
    versions: history.versions,
    saveVersion,
    restoreVersion,
    trash,
    restoreTrash,
    journal,
    productionNotes,
    status,
    speechStatus,
    credentials,
    role,
    notice,
    hasSnapshot,
    networkUrls,
    lanEnabled,
    storageHealthy,
    command,
    scrubTo,
    updateScript,
    selectScript,
    createScript,
    duplicateScript,
    deleteScript,
    importScripts,
    exportScript,
    exportBatch,
    exportBackup,
    importBackup,
    updateSettings,
    selectScrollMode,
    startSpeech,
    stopSpeech,
    feedTranscript,
    transcribeFile,
    cancelAudio,
    audioStatus,
    microphones,
    loadMicrophones,
    microphoneId,
    setMicrophoneId: (id: string) => { stopSpeech(); setMicrophoneId(id); },
    speechModel,
    setSpeechModel,
    rehearsal,
    recording,
    connectRelay,
    openOutput,
    getViewUrl,
    copyText,
    notify,
  };
}
